import { useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useUI } from '@/store/ui'
import { DiwaAPI, ApiError } from '@/api/client'
import { LandingAtmosphere } from '@/components/LandingAtmosphere'
import { MarketingShell } from '@/components/MarketingShell'
import { DiwaSigil } from '@/components/DiwaSigil'
import './landing.css'
import './waitlist.css'

// P16-T05 — pre-launch registration gate (public, pre-auth).
// Registration opens at launch, May 1, 2027 (moved from October 2026 by the
// founder, 2026-10-30). Until then this captures a waitlist and
// lets holders of an early-access code cross into signup. On-brand with the
// landing's "Threshold Observatory" language (dwl- scope), not the app shell.
//
// FOLLOW-UP (deferred): the "warp entry" visual — the crossing-the-threshold
// animation on a successful code — is owned by the diwata-site warp and is NOT
// built here. The success panel carries data-warp="entry" as the future hook.
// Canon: Diwata-Brand/brand/identity/web_design_language.md

interface JoinResponse {
  status: 'joined' | 'already'
  email: string
}

interface ValidateCodeResponse {
  ok: boolean
  reason?: string | null
  message?: string | null
  note?: string | null
}

type JoinState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'joined'; already: boolean }
  | { kind: 'error'; message: string }

type CodeState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; code: string; note?: string | null }
  | { kind: 'invalid'; message: string }

/** The two threshold flows — join the waitlist, or step through with a code.
 *  Reused by both the standalone page and the section embedded in the landing.
 *  `plan` is founding-price intent (a pricing CTA carries ?plan=<tier>); it
 *  rides the join's provenance so the desk can honor the tier at open. */
export function WaitlistForms({ plan }: { plan?: string | null }) {
  const diwaUrl = useUI((s) => s.diwaUrl)
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])

  const [email, setEmail] = useState('')
  const [join, setJoin] = useState<JoinState>({ kind: 'idle' })

  const [code, setCode] = useState('')
  const [codeState, setCodeState] = useState<CodeState>({ kind: 'idle' })

  async function submitJoin(e: FormEvent) {
    e.preventDefault()
    if (join.kind === 'loading') return
    setJoin({ kind: 'loading' })
    try {
      const res = await api.post<JoinResponse>('/waitlist/join', {
        email: email.trim(),
        source: plan ? `landing:plan=${plan}` : 'landing',
      })
      setJoin({ kind: 'joined', already: res.status === 'already' })
    } catch (err) {
      const message =
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Something went wrong. Try again in a moment.'
      setJoin({ kind: 'error', message })
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault()
    if (codeState.kind === 'loading') return
    const trimmed = code.trim()
    if (!trimmed) return
    setCodeState({ kind: 'loading' })
    try {
      // Peek only — the code is SPENT later, at signup. Validating here just
      // tells the visitor whether the door will open before we send them to it.
      const res = await api.post<ValidateCodeResponse>('/waitlist/validate-code', {
        code: trimmed,
      })
      if (res.ok) {
        setCodeState({ kind: 'ok', code: trimmed, note: res.note })
      } else {
        setCodeState({
          kind: 'invalid',
          message: res.message ?? "That access code isn't valid.",
        })
      }
    } catch {
      setCodeState({ kind: 'invalid', message: 'Could not check that code. Try again.' })
    }
  }

  return (
    <div className="dwl-wl-grid">
      {/* ── Join the waitlist ─────────────────────────────────────────── */}
      <div className="dwl-wl-card dwl-ticks">
        <h3 className="dwl-wl-card-title">Join the waitlist</h3>
        <p className="dwl-wl-card-lead">
          The doors open May 1, 2027. Leave your email and we&rsquo;ll send your
          key the moment the threshold opens.
        </p>

        {join.kind === 'joined' ? (
          <p className="dwl-wl-note dwl-wl-note--ok" role="status">
            {join.already
              ? 'You’re already on the list. Sit tight, your key is coming.'
              : 'You’re on the list. Watch your inbox for your key.'}
          </p>
        ) : (
          <form className="dwl-wl-form" onSubmit={submitJoin} noValidate>
            <label className="dwl-wl-field">
              <span className="dwl-wl-label">Email</span>
              <input
                className="dwl-wl-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@domain.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            {join.kind === 'error' && (
              <p className="dwl-wl-note dwl-wl-note--err" role="alert">
                {join.message}
              </p>
            )}
            <button className="dwl-btn" type="submit" disabled={join.kind === 'loading'}>
              {join.kind === 'loading' ? 'Adding…' : 'Join the waitlist'}
            </button>
          </form>
        )}
      </div>

      {/* ── Have an access code? ──────────────────────────────────────── */}
      <div className="dwl-wl-card dwl-ticks">
        <h3 className="dwl-wl-card-title">Have an access code?</h3>
        <p className="dwl-wl-card-lead">
          Early access is by invitation. Enter your code to step through and create
          your account now.
        </p>

        {codeState.kind === 'ok' ? (
          // data-warp: the crossing-the-threshold animation hooks here (deferred — see header).
          <div className="dwl-wl-note dwl-wl-note--ok" data-warp="entry" role="status">
            <p>Code accepted{codeState.note ? ` · ${codeState.note}` : ''}. The threshold is open.</p>
            <Link
              className="dwl-btn dwl-wl-continue"
              // Carry the code into signup. When the gate is ON, signup requires
              // and consumes it; when OFF (today), it's harmlessly ignored.
              to={`/login?mode=signup&code=${encodeURIComponent(codeState.code)}`}
            >
              Continue to create your account
            </Link>
          </div>
        ) : (
          <form className="dwl-wl-form" onSubmit={submitCode} noValidate>
            <label className="dwl-wl-field">
              <span className="dwl-wl-label">Access code</span>
              <input
                className="dwl-wl-input dwl-wl-input--code"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="ABCD-1234"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </label>
            {codeState.kind === 'invalid' && (
              <p className="dwl-wl-note dwl-wl-note--err" role="alert">
                {codeState.message}
              </p>
            )}
            <button
              className="dwl-btn dwl-btn--ghost"
              type="submit"
              disabled={codeState.kind === 'loading'}
            >
              {codeState.kind === 'loading' ? 'Checking…' : 'Enter with code'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

/** The landing embeds this — a titled section wrapping the two threshold flows.
 *  id="threshold" is the landing's CTA anchor: every "Join the waitlist" button
 *  on the page lands here. */
export function WaitlistSection() {
  return (
    <section
      id="threshold"
      className="dwl-section dwl-section--tight"
      aria-label="Waitlist and early access"
    >
      <div className="dwl-container" data-reveal>
        <p className="dwl-section-head">The Threshold</p>
        <p className="dwl-section-note">
          Diwa opens to the public on May 1, 2027. Join the waitlist, or step through now
          with an early-access code.
        </p>
        <p className="dwl-section-note dwl-verse-tie">
          Diwa is the first region of the Diwata verse.{' '}
          <a href="https://diwata.domains" target="_blank" rel="noreferrer">
            Meet the rest <span className="dwl-arr">→</span>
          </a>
        </p>
        <WaitlistForms />
      </div>
    </section>
  )
}

/** Standalone /waitlist page. Wraps the flows in the full public frame so the
 *  link stands on its own; a pricing CTA arrives with ?plan=<tier> (founding-
 *  price intent, carried into the join's provenance). */
export function Waitlist() {
  const [searchParams] = useSearchParams()
  const plan = searchParams.get('plan')
  return (
    <MarketingShell>
      <LandingAtmosphere />

      <section className="dwl-page" aria-label="The Threshold">
        <div className="dwl-page-inner" style={{ textAlign: 'center' }}>
          <DiwaSigil size={72} className="dwl-login-sigil" />
          <h1 className="dwl-page-title">The Threshold</h1>
          <p className="dwl-page-sub" style={{ marginInline: 'auto' }}>
            Diwa opens to the public on May 1, 2027. Join the waitlist and we&rsquo;ll send your
            key the day the doors open. If you were given an early-access code, step through now.
          </p>
        </div>
        <div className="dwl-wl-page-body">
          <WaitlistForms plan={plan} />
        </div>
      </section>
    </MarketingShell>
  )
}
